import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Chip, Grid, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

interface CustomRestrictedWordsEditorProps {
    initialWords: string[];
    onSave: (words: string[]) => Promise<{ customRestrictedWords: string[] }>;
    saveLoading: boolean;
}

const CustomRestrictedWordsEditor = ({ initialWords, onSave, saveLoading }: CustomRestrictedWordsEditorProps) => {
    const theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();
    const [inputValue, setInputValue] = useState<string>('');
    const [customWords, setCustomWords] = useState<string[]>([]);

    // Initialize with provided words and sort them alphabetically
    useEffect(() => {
        if (initialWords) {
            const sortedWords = [...initialWords].sort((a, b) => a.localeCompare(b));
            setCustomWords(sortedWords);
        }
    }, [initialWords]);

    // Sort words alphabetically whenever the list changes
    const sortWords = (words: string[]) => {
        return [...words].sort((a, b) => a.localeCompare(b));
    };

    // Check if the current list has changed from the original
    const hasChanged = () => {
        if (!initialWords) return customWords.length > 0;

        const sortedInitial = [...initialWords].sort((a, b) => a.localeCompare(b));
        const sortedCurrent = [...customWords].sort((a, b) => a.localeCompare(b));

        if (sortedInitial.length !== sortedCurrent.length) return true;

        return sortedInitial.some((word, index) => word !== sortedCurrent[index]);
    };

    const handleAddWord = () => {
        const trimmedWord = inputValue.trim().toLowerCase();

        // Validate input
        if (!trimmedWord) {
            return;
        }

        // Check for duplicates
        if (customWords.includes(trimmedWord)) {
            dialog.alert({
                title: 'Duplicate Word',
                message: 'This word is already in the list.',
            });
            return;
        }

        // Add word and sort the list
        const updatedWords = sortWords([...customWords, trimmedWord]);
        setCustomWords(updatedWords);
        setInputValue('');
    };

    const handleRemoveWord = (wordToRemove: string) => {
        const updatedWords = customWords.filter(word => word !== wordToRemove);
        setCustomWords(updatedWords);
    };

    const handleClearAll = () => {
        if (customWords.length === 0) return;

        dialog
            .confirm({
                title: 'Confirm',
                message: 'Are you sure you want to remove all custom restricted words?',
            })
            .then(() => {
                setCustomWords([]);
            })
            .catch(() => {});
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleAddWord();
        }
    };

    const handleSave = () => {
        onSave(customWords);
    };

    return (
        <Stack spacing={2}>
            <Typography variant="body1">
                Add your own custom restricted words that will be monitored alongside the default restricted words list.
            </Typography>

            {/* Input Section */}

            <Stack direction="column" spacing={0}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        label="Add Custom Restricted Word"
                        variant="outlined"
                        fullWidth
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter a word to restrict..."
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleAddWord}
                        disabled={!inputValue.trim()}
                        startIcon={<AddIcon />}
                        sx={{ minWidth: 'auto', px: 2 }}
                    >
                        Add
                    </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    Press Enter or click Add to include the word.
                </Typography>
            </Stack>

            {/* Statistics and Clear Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Custom Words: {customWords.length.toLocaleString()}
                </Typography>
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleClearAll}
                    disabled={customWords.length === 0}
                    startIcon={<DeleteIcon />}
                >
                    Clear All
                </Button>
            </Stack>

            {/* Words Display as Chips */}
            {customWords.length > 0 ? (
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        p: 2,
                        backgroundColor: theme.palette.grey[50],
                        maxHeight: '300px',
                        overflowY: 'auto',
                    }}
                >
                    <Grid container spacing={1}>
                        {customWords.map(word => (
                            <Grid item key={word}>
                                <Chip
                                    label={word}
                                    onDelete={() => handleRemoveWord(word)}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : (
                <Box
                    sx={{
                        border: `1px dashed ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        No custom restricted words added yet. Add some words above to get started.
                    </Typography>
                </Box>
            )}

            {/* Save Button */}
            <LoadingButton
                variant="contained"
                color={hasChanged() ? 'warning' : 'primary'}
                size="large"
                fullWidth
                loading={saveLoading}
                onClick={handleSave}
            >
                <span>Save Custom Restricted Words{hasChanged() ? ' (Changes Detected)' : ''}</span>
            </LoadingButton>
        </Stack>
    );
};

export default CustomRestrictedWordsEditor;
